import getCurrentPageClass from '../utils/_pageClass';

class oGtm extends window.HTMLDivElement {
    constructor() {
        super();
        this.keepEvent = ['view_item', 'begin_checkout', 'checkout_progress'];
        this.cacheSensitiveAttributes = ['client_status', 'login_status', 'user_email', 'user_id'];
        this.pageView = null;
        this.pagesData = document.querySelectorAll('[data-gtm-page-data]');
        this.viewEvents = document.querySelectorAll('[data-gtm-view-event]');
        this.customerPageData = document.querySelector('[data-gtm-customer-data]');
        this.dyPageData = document.querySelectorAll('[data-gtm-dy-data]');
        this.siteLocation = document.querySelector('[data-gtm-site-location]');
        this.checkoutStep = this.checkoutStep.bind(this);
        this.pushEvent = this.pushEvent.bind(this);
        this.pushEvents = this.pushEvents.bind(this);
        this.checkoutProgress = this.checkoutProgress.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.ajaxEvents = this.ajaxEvent.bind(this);
    }

    connectedCallback() {
        this.path = this.getPath();
        this.getPageView();
        this.sendPageData();
        this.observeViewEvent();
        this.viewEvents.forEach((el) => {
            this.viewObserver.observe(el);
        });
        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('gtm:checkoutStep', this.checkoutStep);
        document.addEventListener('gtm:checkoutProgress', this.checkoutProgress);
        document.addEventListener('gtm:pushEvent', this.pushEvent);
        document.addEventListener('gtm:pushEvents', this.pushEvents);
        document.addEventListener('gtm:ajaxEvent', this.ajaxEvent.bind(this));
    }

    ajaxEvent(input) {
        const gtmData = input && input.detail ? input.detail : input;
        const eventObj = { ...this.pageView };
        this.setAttributes(eventObj, gtmData);
        this.sendEvent(eventObj);
    }

    onDocumentClick(e) {
        let target = e.target.closest('[data-gtm-click-event]');
        if (target) {
            const eventName = target.dataset.gtmClickEvent;
            const eventAttribute = `${eventName}_click_name`;
            const eventValue = target.dataset.gtmValue;
            const event = { ...this.pageView };
            event.event = `${eventName}_click`;
            event[eventAttribute] = eventValue;
            this.sendEvent(event);
        }

        target = e.target.closest('[data-gtm-interaction-event]') || e.target.children[0]?.closest('[data-gtm-interaction-event]');
        if (target && this.actualPage) {
            const eventValue = target.dataset.gtmValue;
            const event = JSON.parse(this.actualPage);
            event.event = target.dataset.gtmInteractionEvent;
            event.interaction_type = eventValue;
            this.sendEvent(event);
        }

        target = e.target.closest('[data-gtm-select-event]');
        if (target) {
            const eventName = target.dataset.gtmSelectEvent;
            this.selectEventCallback(target, eventName);
        }
    }

    getViewItemEventName() {
        const datalayer = window.dataLayer;
        for (let i = datalayer.length - 1; i >= 0; i--) {
            const data = datalayer[i];
            if (data.event === 'quick_view_item' || data.event === 'view_item') {
                return data.event;
            }
        }
        return 'quick_view_item';
    }

    updateEventName(dataObj) {
        switch (dataObj.event) {
            case 'view_item_oos':
                dataObj.event = this.getViewItemEventName();
                break;
            default:
        }
    }

    // The page_view event is sent in gtm/init.isml to make sure to send the page_view event before the others
    getPageView() {
        const { dataLayer } = window;
        for (const event of dataLayer) {
            if (event.event === 'page_view') {
                this.pageView = event;
                return;
            }
        }
    }

    sendPageData() {
        this.pagesData.forEach((pageData) => {
            if (pageData && ('send' in pageData.dataset)) {
                const pageDataObj = JSON.parse(pageData.value);
                const customerData = JSON.parse(this.customerPageData.value);
                const event = Object.assign(pageDataObj, customerData); // Manage cache informations
                if ('pageType' in pageData.dataset) {
                    event.page_type = this.pageView.page_type.toLowerCase();
                }
                this.sendEvent(event);
            }
        });

        this.dyPageData.forEach((dyData) => {
            if (dyData && ('send' in dyData.dataset)) {
                const pageDataObj = JSON.parse(dyData.value);
                const event = Object.assign(pageDataObj); // Manage cache informations
                event.keepPagePath = true;
                this.sendEvent(event);
            }
        });
        window.gtmPageDataSent = true;
        document.dispatchEvent(new CustomEvent('gtm:pageDataSent'));
    }

    sendEvent(data, noEcommerce) {
        const dataObj = data;
        if (!data.keepPagePath) {
            dataObj.page = this.path;
        } else {
            delete dataObj.keepPagePath;
        }
        if (this.keepEvent.includes(dataObj.event)) {
            this.actualPage = JSON.stringify(dataObj);
        }
        if (dataObj.ecommerce && !noEcommerce) {
            window.dataLayer.push({ ecommerce: null });
        }
        this.formatFields(dataObj);
        this.updateEventName(dataObj);
        window.dataLayer.push(dataObj);
    }

    setAttributes(data, attributes) {
        const dataObj = data;
        if (!attributes) {
            return;
        }
        for (const attr of Object.keys(attributes)) {
            if (typeof attributes[attr] === 'object') {
                this.setAttributes(dataObj[attr], attributes[attr]);
            } else {
                dataObj[attr] = attributes[attr];
            }
        }
    }

    pushEvents(e) {
        const { events } = e.detail;
        for (const event of events) {
            this.pushEvent(event);
        }
    }

    pushEvent(event) {
        const details = event.detail || event;
        let { data } = details;
        if (details.usePageView) {
            data = { ...this.pageView };
        }
        if (details.eventName) {
            const { eventName } = details;
            data.event = eventName;
        }
        this.setAttributes(data, details.attributes);
        if (this.pageView) {
            if (details.pageType) {
                data.page_type = this.pageView.page_type;
            }
            data.top_category = this.pageView.top_category;
            data.mid_category = this.pageView.mid_category;
        }
        // Specific case for select_item event
        if (details.getListInfo && details.target) {
            const item = details.target;
            const productList = item.closest('[data-product-list-container]');
            const listEvent = productList && productList.querySelector('[data-gtm-product-list-data]');
            if (listEvent) {
                const listEventObj = JSON.parse(listEvent.value);
                if (listEventObj && listEventObj.ecommerce && data.ecommerce) {
                    data.ecommerce.item_list_id = listEventObj.ecommerce.item_list_id;
                    data.ecommerce.item_list_name = listEventObj.ecommerce.item_list_name;
                }
            } else if (data.ecommerce) {
                data.ecommerce.item_list_id = undefined;
                data.ecommerce.item_list_name = 'Search Results'; // Default case for now, to modify if this value need to be undefined of other values needed
            }
        }
        this.sendEvent(data, details.noEcommerce);
    }

    checkoutProgress(e) {
        if (this.actualPage) {
            const event = JSON.parse(this.actualPage);
            event.event = 'checkout_progress';
            if (e && e.detail) {
                this.setAttributes(event, e.detail.attributes);
            }
            this.sendEvent(event);
        }
    }

    checkoutStep(e) {
        if (this.actualPage) {
            const event = JSON.parse(this.actualPage);
            event.event = e.detail.eventName;
            this.setAttributes(event, e.detail.attributes);
            if (event.checkout_progress_number) {
                delete event.checkout_progress_number;
            }
            this.sendEvent(event);
        }
    }

    formatFields(data) {
        for (const fieldName of Object.keys(data)) {
            const field = data[fieldName];
            if (typeof field === 'object' && field) {
                this.formatFields(field);
            }
            if (fieldName === 'site_location') {
                // eslint-disable-next-line no-param-reassign
                data[fieldName] = this.siteLocation ? this.siteLocation.value : data.page_type;
            } else if (field === 'undefined') {
                // eslint-disable-next-line no-param-reassign
                data[fieldName] = undefined;
            }
        }
    }

    getPath() {
        const url = new URL(window.location.href);
        return url.pathname;
    }

    selectEventCallback(target, eventName) {
        const eventToSend = { ...this.pageView };
        eventToSend.event = `select_${eventName}`;
        eventToSend.ecommerce = { items: [] };
        let eventData;
        switch (eventName) {
            case 'promotion':
                if (!target.dataset.gtmData) {
                    break;
                }
                eventData = JSON.parse(target.dataset.gtmData);
                var url = target.href;
                if (!url) {
                    const link = target.querySelector('a');
                    url = (link && link.href) || undefined;
                }
                eventToSend.url_destination = url;
                eventToSend.ecommerce.items.push(eventData);
                this.sendEvent(eventToSend);
                break;
            case 'item':
                if (!target.dataset.gtmProductData) {
                    break;
                }
                eventData = JSON.parse(target.dataset.gtmProductData);
                var itemList = target.closest('[data-gtm-view-event="item_list"]');
                if (itemList) {
                    eventToSend.ecommerce.item_list_name = itemList.dataset.itemListName;
                    eventToSend.ecommerce.currency = itemList.dataset.currency;
                }
                var discount = eventData.discount !== 'undefined' ? eventData.discount : 0;
                eventToSend.ecommerce.value = eventData.price - discount;
                eventToSend.ecommerce.items.push(eventData);
                this.sendEvent(eventToSend);
                break;
            default:
                break;
        }
    }

    viewEventCallback(eventName, eventData) {
        let eventToSend;
        switch (eventName) {
            case 'promotion':
                eventToSend = { ...this.pageView };
                eventToSend.event = `view_${eventName}`;
                var items = [];
                for (const data of eventData) {
                    items.push(data.data);
                }
                eventToSend.ecommerce = { items };
                this.sendEvent(eventToSend);
                break;
            case 'item_list':
                if (!eventData.length) {
                    break;
                }
                for (const list of eventData) {
                    eventToSend = list.data; // Deep copy
                    const { target } = list;
                    eventToSend.page_type = this.pageView.page_type;
                    eventToSend.top_category = this.pageView.top_category;
                    eventToSend.mid_category = this.pageView.mid_category;
                    const products = target.querySelectorAll('[data-gtm-product-data]');
                    if (products.length && eventToSend.ecommerce && eventToSend.ecommerce.items) {
                        const isHome = getCurrentPageClass() === 'home';
                        eventToSend.ecommerce.item_list_name = eventToSend.ecommerce.item_list_name.replace('PAGE_TYPE', isHome ? 'Home Page' : 'Page Designer');
                        target.dataset.itemListName = eventToSend.ecommerce.item_list_name;
                        target.dataset.currency = eventToSend.ecommerce.currency;
                        products.forEach((product) => {
                            eventToSend.ecommerce.items.push(JSON.parse(product.dataset.gtmProductData));
                        });
                        this.sendEvent(eventToSend);
                    }
                }
                break;
            default:
                break;
        }
    }

    observeViewEvent() {
        const options = {
            threshold: 0.2,
        };

        this.viewObserver = new IntersectionObserver((entries) => {
            const items = {};
            entries.forEach((entry) => {
                if (entry.intersectionRatio > 0) {
                    const element = entry.target;
                    const data = element.dataset.gtmData;
                    const eventName = element.dataset.gtmViewEvent;
                    if (data) {
                        if (!items[eventName]) {
                            items[eventName] = [];
                        }
                        items[eventName].push({ data: JSON.parse(data), target: element });
                        this.viewObserver.unobserve(element);
                    }
                }
            });
            for (const event of Object.keys(items)) {
                this.viewEventCallback(event, items[event]);
            }
        }, options);
    }
}

customElements.define('o-gtm', oGtm, {
    extends: 'div',
});
